import React, { useState, useEffect, useMemo } from 'react'
import { wellService } from '../supabase'
import './ConstructionRecordForm.css' // 重用相同的樣式

const WellForm = ({ isOpen, onClose, onSuccess, editData = null }) => {
  const isEditMode = editData !== null

  const getInitialFormData = () => {
    const defaultData = {
      name: '',
      latitude: '',
      longitude: '',
      e_num: '',
      water_certificate: '',
      well_key: '',
      dry_well: '',
      kw: '',
      electric_meter_multiples: '',
      unit: '',
      irrigation_canal: '',
      '30m_up_or_down': '',
      area: '',
      location: '',
      place_number: '',
      note: '',
      number_of_unit: '',
      hp_number_of_unit_condition: '',
      hp: '',
      motor_type: '',
      pump_depth: '',
      TM2_X_67: '',
      TM2_Y_67: '',
      TM2_X_97: '',
      TM2_Y_97: '',
      land_area_used: '',
      owner: '',
      use_mothod: '',
      stratigraphic_map_and_water_testing: '',
      well_tube_material: '',
      well_diameter_inch: '',
      well_deep_m: '',
      filter_pipe_length_m: '',
      installation_depth_of_filter_pipe_m: '',
      water_level_observation_tube: '',
      water_level_observation_tube_m: '',
      well_setting_date: '',
      ori_water_volume_cmm: '',
      ori_measurement_related_information: '',
      water_volume_cmm: '',
      measurement_related_information: '',
      note2: '',
      standard_hydraulic_head_x_standard_water_volume: '',
      'outlet_pipe_diameter(inch)': '',
      outlet_pipe_material: '',
      'installation_depth_of water_level_observation_pipe(m)': '',
      'static_water_level(m)': '',
      failure_reason: '',
      repair_date: '',
      vendor: ''
    }

    if (isEditMode && editData) {
      // 編輯模式：使用現有資料，null 值轉換為空字符串
      const populatedData = { ...defaultData }
      Object.keys(populatedData).forEach(key => {
        if (editData[key] !== undefined && editData[key] !== null) {
          populatedData[key] = editData[key]
        }
      })
      return populatedData
    }

    return defaultData
  }

  // 使用 useRef 來保持 getInitialFormData 的引用，或者將其移到組件外部
  // 這裡我們將其移到 useEffect 內部直接調用，或者簡單地不依賴它

  const [formData, setFormData] = useState(getInitialFormData())

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // 單位搜尋下拉相關狀態
  const [existingUnits, setExistingUnits] = useState([])
  const [showUnitOptions, setShowUnitOptions] = useState(false)

  // 載入現有單位列表
  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const wellsData = await wellService.getWellsOnly()
        if (wellsData) {
          const units = wellsData
            .map(w => w.unit)
            .filter(u => u && u.trim() !== '')
          setExistingUnits([...new Set(units)].sort())
        }
      } catch (err) {
        console.error('無法載入單位列表:', err)
      }
    }
    fetchUnits()
  }, [])

  // 過濾後的單位列表
  const filteredUnits = useMemo(() => {
    if (!formData.unit) return existingUnits
    return existingUnits.filter(unit =>
      unit.toLowerCase().includes(formData.unit.toLowerCase())
    )
  }, [formData.unit, existingUnits])

  // 處理單位輸入框焦點
  const handleUnitInputFocus = () => {
    setShowUnitOptions(true)
  }

  // 處理單位輸入框失去焦點
  const handleUnitInputBlur = () => {
    setTimeout(() => {
      setShowUnitOptions(false)
    }, 200)
  }

  // 處理選擇單位
  const handleSelectUnit = (unit) => {
    setFormData(prev => ({
      ...prev,
      unit: unit
    }))
    setShowUnitOptions(false)
  }

  // 當 editData 變化時重新初始化表單
  useEffect(() => {
    setFormData(getInitialFormData())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editData, isEditMode])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      // 檢查必填欄位
      if (!formData.name) {
        setError('名稱為必填欄位')
        setLoading(false)
        return
      }

      // 根據模式決定新增或更新
      let result
      if (isEditMode) {
        result = await wellService.updateWell(editData.id, formData)
      } else {
        result = await wellService.createWell(formData)
      }

      // 只在新增模式下清空表單
      if (!isEditMode) {
        setFormData(getInitialFormData())
      }

      // 成功回調
      if (onSuccess) {
        onSuccess(result)
      }

      alert(isEditMode ? '水井更新成功！' : '水井新增成功！')
      onClose()

    } catch (error) {
      console.error('提交失敗:', error)
      setError('提交失敗：' + error.message)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>{isEditMode ? '編輯水井' : '新增水井'}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="construction-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-section">
            <h3>基本資訊</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>名稱</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>緯度</label>
                <input
                  type="text"
                  name="latitude"
                  value={formData.latitude}
                  onChange={handleChange}
                  placeholder="例：23.123456"
                />
              </div>
              <div className="form-group">
                <label>經度</label>
                <input
                  type="text"
                  name="longitude"
                  value={formData.longitude}
                  onChange={handleChange}
                  placeholder="例：120.123456"
                />
              </div>
              <div className="form-group">
                <label>單位 <span style={{ color: '#e74c3c' }}>*</span></label>
                <div className="searchable-select">
                  <input
                    type="text"
                    name="unit"
                    value={formData.unit}
                    onChange={handleChange}
                    required
                    onFocus={handleUnitInputFocus}
                    onBlur={handleUnitInputBlur}
                    placeholder="輸入或選擇單位"
                    autoComplete="off"
                  />
                  {showUnitOptions && filteredUnits.length > 0 && (
                    <div className="options-dropdown">
                      {filteredUnits.map((unit, index) => (
                        <div
                          key={index}
                          className="option-item"
                          onClick={() => handleSelectUnit(unit)}
                        >
                          <div className="well-name">{unit}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label>馬力</label>
                <input
                  type="text"
                  name="hp"
                  value={formData.hp}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>馬達類型</label>
                <select
                  name="motor_type"
                  value={formData.motor_type}
                  onChange={handleChange}
                >
                  <option value="">請選擇馬達類型</option>
                  <option value="沉水式">沉水式</option>
                  <option value="立軸式">立軸式</option>
                  <option value="污水">污水</option>
                </select>
              </div>

            </div>
          </div>

          <div className="form-section">
            <h3>電力、水資訊</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>電號</label>
                <input
                  type="text"
                  name="e_num"
                  value={formData.e_num}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>水權狀號</label>
                <input
                  type="text"
                  name="water_certificate"
                  value={formData.water_certificate}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>契約容量(kw)</label>
                <input
                  type="text"
                  name="kw"
                  value={formData.kw}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>電表倍數</label>
                <input
                  type="text"
                  name="electric_meter_multiples"
                  value={formData.electric_meter_multiples}
                  onChange={handleChange}
                />
              </div>

            </div>
          </div>
          <div className="form-section">
            <h3>設備資訊</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>pump安裝深度</label>
                <input
                  type="text"
                  name="pump_depth"
                  value={formData.pump_depth}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>出水管徑(英寸)</label>
                <input
                  type="text"
                  name="outlet_pipe_diameter(inch)"
                  value={formData['outlet_pipe_diameter(inch)']}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>出水管材質</label>
                <input
                  type="text"
                  name="outlet_pipe_material"
                  value={formData.outlet_pipe_material}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>標準揚程×標準水量</label>
                <input
                  type="text"
                  name="standard_hydraulic_head_x_standard_water_volume"
                  value={formData.standard_hydraulic_head_x_standard_water_volume}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
          <div className="form-section">
            <h3>井體規格</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>井類型(深井、淺井、地面水)</label>
                <input
                  type="text"
                  name="30m_up_or_down"
                  value={formData['30m_up_or_down']}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>井管材質</label>
                <input
                  type="text"
                  name="well_tube_material"
                  value={formData.well_tube_material}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>井徑(英寸)</label>
                <input
                  type="text"
                  name="well_diameter_inch"
                  value={formData.well_diameter_inch}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>井深(公尺)</label>
                <input
                  type="text"
                  name="well_deep_m"
                  value={formData.well_deep_m}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>濾管長度(公尺)</label>
                <input
                  type="text"
                  name="filter_pipe_length_m"
                  value={formData.filter_pipe_length_m}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>濾管安裝深度(公尺)</label>
                <input
                  type="text"
                  name="installation_depth_of_filter_pipe_m"
                  value={formData.installation_depth_of_filter_pipe_m}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>井體設置日期</label>
                <input
                  type="date"
                  name="well_setting_date"
                  value={formData.well_setting_date}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>水量水位資訊</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>水位觀測管</label>
                <input
                  type="text"
                  name="water_level_observation_tube"
                  value={formData.water_level_observation_tube}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>水位觀測管深度(公尺)</label>
                <input
                  type="text"
                  name="water_level_observation_tube_m"
                  value={formData.water_level_observation_tube_m}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>水位觀測管安裝深度(公尺)</label>
                <input
                  type="text"
                  name="installation_depth_of water_level_observation_pipe(m)"
                  value={formData['installation_depth_of water_level_observation_pipe(m)']}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>靜水位(公尺)</label>
                <input
                  type="text"
                  name="static_water_level(m)"
                  value={formData['static_water_level(m)']}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>原始水量(cmm)</label>
                <input
                  type="text"
                  name="ori_water_volume_cmm"
                  value={formData.ori_water_volume_cmm}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>水量(cmm)</label>
                <input
                  type="text"
                  name="water_volume_cmm"
                  value={formData.water_volume_cmm}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>



          <div className="form-section">
            <h3>座標資訊</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>TM2_X_67</label>
                <input
                  type="text"
                  name="TM2_X_67"
                  value={formData.TM2_X_67}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>TM2_Y_67</label>
                <input
                  type="text"
                  name="TM2_Y_67"
                  value={formData.TM2_Y_67}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>TM2_X_97</label>
                <input
                  type="text"
                  name="TM2_X_97"
                  value={formData.TM2_X_97}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>TM2_Y_97</label>
                <input
                  type="text"
                  name="TM2_Y_97"
                  value={formData.TM2_Y_97}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>其他資訊</h3>
            <div className="form-grid">

              <div className="form-group">
                <label>水井代碼(百修資料表)</label>
                <input
                  type="text"
                  name="well_key"
                  value={formData.well_key}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>區域</label>
                <input
                  type="text"
                  name="area"
                  value={formData.area}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>位置</label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>地號</label>
                <input
                  type="text"
                  name="place_number"
                  value={formData.place_number}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>是否乾井</label>
                <input
                  type="text"
                  name="dry_well"
                  value={formData.dry_well}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>灌溉渠道</label>
                <input
                  type="text"
                  name="irrigation_canal"
                  value={formData.irrigation_canal}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>單位數量</label>
                <input
                  type="text"
                  name="number_of_unit"
                  value={formData.number_of_unit}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>馬力單位條件</label>
                <input
                  type="text"
                  name="hp_number_of_unit_condition"
                  value={formData.hp_number_of_unit_condition}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>使用土地面積</label>
                <input
                  type="text"
                  name="land_area_used"
                  value={formData.land_area_used}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>所有人</label>
                <input
                  type="text"
                  name="owner"
                  value={formData.owner}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>使用方法</label>
                <input
                  type="text"
                  name="use_mothod"
                  value={formData.use_mothod}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>地層圖及水質檢測</label>
                <input
                  type="text"
                  name="stratigraphic_map_and_water_testing"
                  value={formData.stratigraphic_map_and_water_testing}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>原始測量相關資訊</label>
                <textarea
                  name="ori_measurement_related_information"
                  value={formData.ori_measurement_related_information}
                  onChange={handleChange}
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label>測量相關資訊</label>
                <textarea
                  name="measurement_related_information"
                  value={formData.measurement_related_information}
                  onChange={handleChange}
                  rows="3"
                />
              </div>


              <h3>備註</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>備註</label>
                  <textarea
                    name="note"
                    value={formData.note}
                    onChange={handleChange}
                    rows="3"
                  />
                </div>
                <div className="form-group">
                  <label>備註2</label>
                  <textarea
                    name="note2"
                    value={formData.note2}
                    onChange={handleChange}
                    rows="3"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>廠商</label>
                <input
                  type="text"
                  name="vendor"
                  value={formData.vendor}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>
              取消
            </button>
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? '提交中...' : '提交'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default WellForm 